/**
 * useAuthStatus - Single source of truth for authentication state
 *
 * Modern React pattern: One hook that determines the user's auth status,
 * eliminating race conditions and complex state checks scattered across components.
 *
 * States:
 * - loading: Still determining auth state (Clerk loading, fetching user, etc.)
 * - unauthenticated: User is not signed in
 * - needsRegistration: User is signed in but profile is incomplete
 * - authenticated: User is fully authenticated with complete profile
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import { isProfileComplete } from "../utils/profileValidation";

// Enum-like constants for auth status
export const AuthStatus = {
  LOADING: "loading",
  UNAUTHENTICATED: "unauthenticated",
  NEEDS_REGISTRATION: "needsRegistration",
  AUTHENTICATED: "authenticated",
};

export const useAuthStatus = () => {
  const { isLoaded: clerkLoaded, isSignedIn, user: clerkUser } = useUser();
  const reduxUser = useSelector((state) => state.loggedUser?.user);

  // Derive auth status from actual data - no redundant flags needed
  const authStatus = useMemo(() => {
    // 1. Clerk still loading
    if (!clerkLoaded) {
      return AuthStatus.LOADING;
    }

    // 2. Not signed in with Clerk
    if (!isSignedIn) {
      return AuthStatus.UNAUTHENTICATED;
    }

    // 3. Signed in but no Redux user yet (backend sync in progress)
    //    Check for clerk_user_id since new users may have null id until registered
    if (!reduxUser?.clerk_user_id) {
      return AuthStatus.LOADING;
    }

    // 4. Stale state: Redux user doesn't match current Clerk user
    //    This happens when a different user signs in
    if (reduxUser.clerk_user_id !== clerkUser?.id) {
      return AuthStatus.LOADING;
    }

    // 5. Check profile completeness - this is THE source of truth
    const profileComplete = isProfileComplete(reduxUser);

    return profileComplete
      ? AuthStatus.AUTHENTICATED
      : AuthStatus.NEEDS_REGISTRATION;
  }, [clerkLoaded, isSignedIn, clerkUser?.id, reduxUser]);

  return {
    status: authStatus,
    isLoading: authStatus === AuthStatus.LOADING,
    isUnauthenticated: authStatus === AuthStatus.UNAUTHENTICATED,
    needsRegistration: authStatus === AuthStatus.NEEDS_REGISTRATION,
    isAuthenticated: authStatus === AuthStatus.AUTHENTICATED,
    user: reduxUser,
    clerkUser,
  };
};
